import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useProduct } from "@/hooks/useProducts";
import { useLang, productPath } from "@/lib/i18n";
import { euro } from "@/lib/api";
import { VideoSpotlight } from "@/components/site/ProductVideo";

export const LUMINA_SLUG = "serum-anti-taches-lumina-vg";
export const LUMINA_IMG = "/products/real/lumina-vg-serum.webp";
const LUMINA_CAMPAIGN = "/products/real/lumina-vg-campagne.webp";
const BRONZE = "#7A5418";

const TXT = {
  fr: {
    eyebrow: "Ozentya — Beauté & Éclat · Nouveauté en exclusivité", title: "LUMINA V.G. : le sérum anti-taches qui révèle", name: "l'éclat de votre peau",
    lead: "Découvrez une nouvelle approche du soin du teint avec notre sérum enrichi en niacinamide, alpha-arbutine et acide tranexamique.",
    offer: "Offre de lancement exclusive", save: (n) => `Économisez ${n} sur le prix habituel.`, pre: "Faites vos précommandes dès maintenant !", now: "Disponible dès maintenant !",
    cta: "Découvrir Lumina V.G.", sheet: "Explorer nos soins", sheetTo: "/boutique", format: "Flacon pipette 30 ml",
    actives: [["Niacinamide", "Vitamine B3"], ["Alpha-arbutine", "Teint plus homogène"], ["Acide tranexamique", "Cible l'apparence des taches"]],
  },
  en: {
    eyebrow: "Ozentya — Beauty & Radiance · New", title: "LUMINA V.G.: the dark spot serum that reveals", name: "your skin's radiance",
    lead: "Discover a new approach to complexion care with our serum enriched with niacinamide, alpha-arbutin and tranexamic acid.",
    offer: "Exclusive launch offer", save: (n) => `Save ${n} on the regular price.`, pre: "Pre-order yours now!", now: "Available now!",
    cta: "Discover Lumina V.G.", sheet: "Explore our care", sheetTo: "/en/shop", format: "30 ml dropper bottle",
    actives: [["Niacinamide", "Vitamin B3"], ["Alpha-arbutin", "More even-looking tone"], ["Tranexamic acid", "Targets the look of spots"]],
  },
};

function Glow() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -right-24 top-1/4 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(217,180,106,0.38),transparent_65%)] blur-2xl" />
      <div className="absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.9),transparent_70%)]" />
      <div className="lumina-shine absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
    </div>
  );
}

function Actives({ list }) {
  return (
    <ul data-testid="lumina-actives" className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {list.map(([n, s], k) => (
        <li key={n} data-testid={`lumina-active-${k}`} className="rounded-sm border border-[#B8893A]/30 bg-white/60 px-4 py-4 backdrop-blur-md">
          <span className="font-display text-xl italic text-[#141013]">{n}</span>
          <span className="mt-1 block text-xs uppercase tracking-[0.16em]" style={{ color: BRONZE }}>{s}</span>
        </li>
      ))}
    </ul>
  );
}

/** Homepage launch banner for the Lumina V.G serum (prices read from the catalogue; hidden if the product is off). */
export function LuminaLaunch() {
  const { lang } = useLang();
  const { data: p } = useProduct(LUMINA_SLUG);
  if (!p?.slug) return null;
  const T = TXT[lang === "en" ? "en" : "fr"];
  const href = productPath(p.slug, lang);
  const save = p.compare_at > p.price ? p.compare_at - p.price : 0;
  return (
    <section data-testid="lumina-launch" className="relative overflow-hidden bg-[linear-gradient(135deg,#FBF8F3_0%,#F3E8D6_55%,#EAD9BC_100%)] text-[#141013]">
      <Glow />
      <div className="relative mx-auto grid max-w-[1440px] items-center gap-10 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12">
        <p data-testid="lumina-eyebrow" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] lg:col-span-12" style={{ color: BRONZE }}>
          <Sparkles className="h-4 w-4" /> {T.eyebrow} <Sparkles className="h-4 w-4" />
        </p>
        <Link to={href} data-testid="lumina-image-link" className="relative mx-auto block w-full max-w-[460px] lg:order-2 lg:col-span-5 lg:max-w-none">
          <div aria-hidden="true" className="absolute -inset-6 rounded-sm bg-[radial-gradient(circle,rgba(217,180,106,0.45),transparent_70%)] blur-2xl" />
          <img src={LUMINA_CAMPAIGN} alt={lang === "en" ? "Two women with radiant skin and the Ozentya LUMINA V.G. dark spot serum" : "Deux femmes au teint lumineux et le sérum anti-taches LUMINA V.G. d'Ozentya"} width="928" height="1152" loading="lazy"
            data-testid="lumina-campaign-img" className="relative aspect-[928/1152] w-full rounded-sm border border-[#B8893A]/40 object-cover shadow-[0_40px_90px_-30px_rgba(74,40,10,0.55)] transition-transform duration-700 hover:scale-[1.02]" />
        </Link>
        <div className="lg:order-1 lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-[#141013]/70">Ozentya · {T.format}</p>
          <h2 data-testid="lumina-title" className="mt-3 font-display text-4xl leading-[1.05] sm:text-5xl xl:text-6xl">
            {T.title} <span className="italic" style={{ color: BRONZE }}>{T.name}</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#141013]/80 md:text-lg">{T.lead}</p>
          <div data-testid="lumina-offer" className="mt-8 inline-flex flex-wrap items-baseline gap-x-5 gap-y-2 rounded-sm border border-[#B8893A]/40 bg-white/70 px-6 py-5 backdrop-blur-md">
            <span className="w-full text-[11px] font-semibold uppercase tracking-[0.26em]" style={{ color: BRONZE }}>{T.offer}</span>
            {p.compare_at ? <span data-testid="lumina-compare-price" className="text-xl text-[#141013]/60 line-through">{euro(p.compare_at)}</span> : null}
            <span data-testid="lumina-price" className="font-display text-5xl text-[#141013]">{euro(p.price)}</span>
            {save ? <span data-testid="lumina-save" className="w-full text-sm text-[#141013]/80">{T.save(euro(save))}</span> : null}
          </div>
          <p data-testid="lumina-availability" className="mt-5 text-sm font-semibold">{p.preorder ? T.pre : T.now}</p>
          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to={href} data-testid="lumina-cta" className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#141013] px-8 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--gold-soft)] transition-[background-color,transform] duration-300 hover:bg-[var(--plum)] active:scale-[0.98]">
              {T.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to={T.sheetTo} data-testid="lumina-sheet-link" className="text-center text-sm underline underline-offset-4" style={{ color: BRONZE }}>{T.sheet}</Link>
          </div>
          <Actives list={T.actives} />
        </div>
      </div>
    </section>
  );
}

/** Homepage highlight of the Pack Pouss Optimal with its campaign video. */
export function PoussSpotlight() {
  const { lang } = useLang();
  const en = lang === "en";
  return (
    <VideoSpotlight slug="pack-pouss-optimal" testid="pouss-spotlight" reverse
      eyebrow={en ? "Signature routine · 5 products" : "Routine signature · 5 soins"}
      title="Pack Pouss Optimal"
      text={en ? "Extrême Repouss, Cactus shampoo, conditioner, Cactus cream and Masque Suprême: a complete 5-step routine for fragile hair and thinning areas." : "Extrême Repouss, Shampoing Cactus, Après-shampoing, Crème Cactus et Masque Suprême : une routine complète en 5 gestes pour les cheveux fragilisés et les zones clairsemées."}
      cta={en ? "Discover the pack" : "Découvrir le pack"} />
  );
}
